import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, ShoppingCart, Users, Mail, TrendingUp, Package,
  Euro, Calculator, Calendar, Clock, Bell, Download, RefreshCw,
  ChevronRight, AlertCircle, Check, X, MessageSquare, LogOut,
  Settings, UserPlus, Key, Shield, Sparkles
} from 'lucide-react';
import AdminSuggestions from './AdminSuggestions';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('manager_token'));
  const [manager, setManager] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [subscribers, setSubscribers] = useState([]);
  const [customerEmails, setCustomerEmails] = useState([]);
  const [showSubscribers, setShowSubscribers] = useState(false);
  const [showEmails, setShowEmails] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/manager/login');
      return;
    }
    loadDashboardData();
  }, [token, navigate]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/manager/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Dashboard load error:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('manager_token');
        navigate('/manager/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSubscribers = async () => {
    try {
      const response = await axios.get(`${API}/manager/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data.subscribers);
      setShowSubscribers(true);
    } catch (error) {
      toast.error('Laden mislukt');
    }
  };

  const loadCustomerEmails = async () => {
    try {
      const response = await axios.get(`${API}/manager/customers/emails`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerEmails(response.data.customers);
      setShowEmails(true);
    } catch (error) {
      toast.error('Laden mislukt');
    }
  };

  const testWhatsApp = async () => {
    try {
      const response = await axios.post(`${API}/manager/test-whatsapp`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('WhatsApp test bericht verzonden!');
      } else {
        toast.error('WhatsApp test mislukt');
      }
    } catch (error) {
      toast.error('WhatsApp test mislukt');
    }
  };

  const exportToCSV = (data, filename) => {
    const headers = Object.keys(data[0] || {}).join(',');
    const rows = data.map(item => Object.values(item).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const handleLogout = () => {
    localStorage.removeItem('manager_token');
    navigate('/manager/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#1C1917] border-b border-[#2C1B18] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">
              <span className="text-[#D4AF37]">ZuiverParfum</span>
              <span className="text-white ml-2">Manager</span>
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={loadDashboardData}
              className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E] hover:text-white transition-colors"
              title="Vernieuwen"
            >
              <RefreshCw size={20} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              Uitloggen
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Orders */}
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <ShoppingCart size={20} className="text-blue-400" />
              </div>
              <span className="text-[#A8A29E] text-sm">Bestellingen</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.orders?.total || 0}</p>
            <p className="text-xs text-green-400">{stats?.orders?.paid || 0} betaald</p>
          </div>

          {/* Revenue Today */}
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#D4AF37]/10 rounded-lg">
                <Euro size={20} className="text-[#D4AF37]" />
              </div>
              <span className="text-[#A8A29E] text-sm">Omzet Vandaag</span>
            </div>
            <p className="text-2xl font-bold text-white">€{stats?.revenue?.today?.toFixed(2) || '0.00'}</p>
          </div>

          {/* Customers */}
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Users size={20} className="text-purple-400" />
              </div>
              <span className="text-[#A8A29E] text-sm">Klanten</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.customers?.total || 0}</p>
            <p className="text-xs text-green-400">+{stats?.customers?.today || 0} vandaag</p>
          </div>

          {/* Newsletter */}
          <div className="glass rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-pink-500/10 rounded-lg">
                <Mail size={20} className="text-pink-400" />
              </div>
              <span className="text-[#A8A29E] text-sm">Nieuwsbrief</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.newsletter?.total_subscribers || 0}</p>
            <p className="text-xs text-green-400">+{stats?.newsletter?.this_month || 0} deze maand</p>
          </div>
        </div>

        {/* Revenue Overview */}
        <div className="glass rounded-xl p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="text-[#D4AF37]" />
            Omzet Overzicht
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Dag</p>
              <p className="text-xl font-bold text-white">€{stats?.revenue?.today?.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Week</p>
              <p className="text-xl font-bold text-white">€{stats?.revenue?.week?.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Maand</p>
              <p className="text-xl font-bold text-white">€{stats?.revenue?.month?.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Kwartaal</p>
              <p className="text-xl font-bold text-white">€{stats?.revenue?.quarter?.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Jaar</p>
              <p className="text-xl font-bold text-[#D4AF37]">€{stats?.revenue?.year?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* BTW & Netto Winst */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* BTW */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Calculator className="text-red-400" />
              BTW / Omzetbelasting (21%)
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Vandaag</p>
                <p className="text-lg font-bold text-red-400">€{stats?.btw?.today?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Maand</p>
                <p className="text-lg font-bold text-red-400">€{stats?.btw?.month?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Kwartaal</p>
                <p className="text-lg font-bold text-red-400">€{stats?.btw?.quarter?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Jaar</p>
                <p className="text-lg font-bold text-red-400">€{stats?.btw?.year?.toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Netto Winst */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="text-green-400" />
              Netto Winst (geschat)
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Vandaag</p>
                <p className="text-lg font-bold text-green-400">€{stats?.netto_winst?.today?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Maand</p>
                <p className="text-lg font-bold text-green-400">€{stats?.netto_winst?.month?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Kwartaal</p>
                <p className="text-lg font-bold text-green-400">€{stats?.netto_winst?.quarter?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-[#1C1917] rounded-lg">
                <p className="text-[#A8A29E] text-xs mb-1">Jaar</p>
                <p className="text-lg font-bold text-green-400">€{stats?.netto_winst?.year?.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Klant Statistieken */}
        <div className="glass rounded-xl p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Users className="text-purple-400" />
            Klant Registraties
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Vandaag</p>
              <p className="text-xl font-bold text-white">{stats?.customers?.today || 0}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Week</p>
              <p className="text-xl font-bold text-white">{stats?.customers?.week || 0}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Maand</p>
              <p className="text-xl font-bold text-white">{stats?.customers?.month || 0}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Kwartaal</p>
              <p className="text-xl font-bold text-white">{stats?.customers?.quarter || 0}</p>
            </div>
            <div className="text-center p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-1">Totaal</p>
              <p className="text-xl font-bold text-[#D4AF37]">{stats?.customers?.total || 0}</p>
            </div>
          </div>
        </div>

        {/* Prognose */}
        <div className="glass rounded-xl p-6 mb-8">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="text-[#D4AF37]" />
            Prognose (gebaseerd op laatste 30 dagen)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-2">Dag</p>
              <p className="text-lg font-bold text-white">~{stats?.forecast?.day?.orders} orders</p>
              <p className="text-sm text-[#D4AF37]">€{stats?.forecast?.day?.revenue?.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-2">Week</p>
              <p className="text-lg font-bold text-white">~{stats?.forecast?.week?.orders} orders</p>
              <p className="text-sm text-[#D4AF37]">€{stats?.forecast?.week?.revenue?.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-[#1C1917] rounded-lg">
              <p className="text-[#A8A29E] text-sm mb-2">Maand</p>
              <p className="text-lg font-bold text-white">~{stats?.forecast?.month?.orders} orders</p>
              <p className="text-sm text-[#D4AF37]">€{stats?.forecast?.month?.revenue?.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-[#1C1917] rounded-lg border border-[#D4AF37]/30">
              <p className="text-[#A8A29E] text-sm mb-2">Kwartaal</p>
              <p className="text-lg font-bold text-white">~{stats?.forecast?.quarter?.orders} orders</p>
              <p className="text-sm text-[#D4AF37]">€{stats?.forecast?.quarter?.revenue?.toFixed(2)}</p>
              <p className="text-xs text-red-400 mt-1">BTW: €{stats?.forecast?.quarter?.btw?.toFixed(2)}</p>
              <p className="text-xs text-green-400">Netto: €{stats?.forecast?.quarter?.netto?.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Order Status */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Open Orders */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Package className="text-orange-400" />
              Openstaande Bestellingen ({stats?.orders?.pending || 0})
            </h2>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {stats?.open_orders?.slice(0, 10).map((order, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-[#1C1917] rounded-lg">
                  <div>
                    <p className="text-white text-sm">#{order.id?.slice(0, 8)}</p>
                    <p className="text-[#A8A29E] text-xs">{order.guest_email || 'Account klant'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[#D4AF37] font-bold">€{order.total?.toFixed(2)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      order.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      order.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!stats?.open_orders || stats.open_orders.length === 0) && (
                <p className="text-[#57534E] text-center py-4">Geen openstaande bestellingen</p>
              )}
            </div>
          </div>

          {/* Abandoned Carts */}
          <div className="glass rounded-xl p-6">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <AlertCircle className="text-red-400" />
              Verlaten Winkelwagens ({stats?.orders?.abandoned_carts || 0})
            </h2>
            <p className="text-[#A8A29E] text-sm mb-4">
              Bestellingen gestart maar niet afgerond (ouder dan 1 uur)
            </p>
            <div className="text-center py-8">
              <p className="text-4xl font-bold text-red-400">{stats?.orders?.abandoned_carts || 0}</p>
              <p className="text-[#57534E] text-sm mt-2">potentiële omzet verloren</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <button
            onClick={loadSubscribers}
            className="flex items-center justify-center gap-2 p-4 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/30 rounded-xl text-pink-400 transition-colors"
          >
            <Mail size={20} />
            <span>Nieuwsbrief Lijst</span>
          </button>
          
          <button
            onClick={loadCustomerEmails}
            className="flex items-center justify-center gap-2 p-4 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-xl text-purple-400 transition-colors"
          >
            <Users size={20} />
            <span>Klant E-mails</span>
          </button>
          
          <button
            onClick={testWhatsApp}
            className="flex items-center justify-center gap-2 p-4 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 rounded-xl text-green-400 transition-colors"
          >
            <MessageSquare size={20} />
            <span>Test WhatsApp</span>
          </button>
          
          <button
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="flex items-center justify-center gap-2 p-4 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-xl text-[#D4AF37] transition-colors"
          >
            <Sparkles size={20} />
            <span>Parfum Suggesties</span>
          </button>
        </div>

        {/* Parfum Suggesties Section */}
        {showSuggestions && (
          <div className="glass rounded-xl p-6 mb-8">
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Sparkles className="text-[#D4AF37]" />
              Parfum Suggesties van Klanten
            </h2>
            <AdminSuggestions token={token} isManager={true} />
          </div>
        )}

        {/* WhatsApp Info */}
        <div className="glass rounded-xl p-6 mb-8 border border-green-500/30">
          <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
            <MessageSquare className="text-green-400" />
            WhatsApp Direct Delivery Notificaties
          </h2>
          <p className="text-[#A8A29E] text-sm mb-4">
            Bij elke "Direct Delivery" of "Express" bestelling ontvang je automatisch een WhatsApp bericht op:
          </p>
          <p className="text-[#D4AF37] font-mono text-lg">+31 6 14908691</p>
        </div>
      </div>

      {/* Newsletter Subscribers Modal */}
      {showSubscribers && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Nieuwsbrief Abonnees ({subscribers.length})</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => exportToCSV(subscribers, 'nieuwsbrief_abonnees.csv')}
                  className="p-2 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 rounded-lg text-[#D4AF37]"
                >
                  <Download size={20} />
                </button>
                <button onClick={() => setShowSubscribers(false)} className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E]">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-96">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#1C1917]">
                  <tr className="text-[#A8A29E] text-sm">
                    <th className="text-left p-2">E-mail</th>
                    <th className="text-left p-2">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((sub, index) => (
                    <tr key={index} className="border-t border-[#2C1B18]">
                      <td className="p-2 text-white">{sub.email}</td>
                      <td className="p-2 text-[#A8A29E] text-sm">
                        {new Date(sub.subscribed_at).toLocaleDateString('nl-NL')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Customer Emails Modal */}
      {showEmails && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Klant E-mails ({customerEmails.length})</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => exportToCSV(customerEmails, 'klant_emails.csv')}
                  className="p-2 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 rounded-lg text-[#D4AF37]"
                >
                  <Download size={20} />
                </button>
                <button onClick={() => setShowEmails(false)} className="p-2 hover:bg-white/5 rounded-lg text-[#A8A29E]">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-96">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#1C1917]">
                  <tr className="text-[#A8A29E] text-sm">
                    <th className="text-left p-2">Naam</th>
                    <th className="text-left p-2">E-mail</th>
                    <th className="text-left p-2">Datum</th>
                  </tr>
                </thead>
                <tbody>
                  {customerEmails.map((customer, index) => (
                    <tr key={index} className="border-t border-[#2C1B18]">
                      <td className="p-2 text-white">{customer.first_name} {customer.last_name}</td>
                      <td className="p-2 text-[#D4AF37]">{customer.email}</td>
                      <td className="p-2 text-[#A8A29E] text-sm">
                        {new Date(customer.created_at).toLocaleDateString('nl-NL')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerDashboard;
